/* result
 * -------------------------------------------------------------------------- */

export type Result<T, E> = (
  | { ok: true; value: T }
  | { ok: false; error: E }
);

export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
};

export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
};

export function mapResult<T, E, U>(
  result: Result<T, E>,
  mapper: (value: T) => U
): Result<U, E> {
  return result.ok ? ok(mapper(result.value)) : result;
}

export function flatMapResult<T, E, U>(
  result: Result<T, E>,
  mapper: (value: T) => Result<U, E>
): Result<U, E> {
  return result.ok ? mapper(result.value) : result;
}

export function safeTry<T>(runnable: () => T): Result<T, unknown> {
  try {
    return ok(runnable());

  } catch (error) {
    return err(error);
  }
}

export async function safeTryAsync<T>(runnable: () => Promise<T>): Promise<Result<T, unknown>> {
  try {
    return ok(await runnable());

  } catch (error) {
    return err(error);
  }
}


/* matching
 * -------------------------------------------------------------------------- */

export function matchKey<
  const TKey extends string,
  const TCases extends { [k in NoInfer<TKey>]: () => any }
>
  (key: TKey, cases: string extends TKey ? never : TCases):
    ReturnType<TCases[keyof TCases]>
{
  if (!cases[key]) {
    throw new Error(`Key "${key}" is not handled by your cases. There is a mismatch between types and runtime value. Available cases: ${Object.keys(cases).map(k => `"${k}"`).join(", ")}.`);
  }

  return cases[key]();
}


export function matchKeyPart<
  const TKey extends string,
  const TCases extends { [k in NoInfer<TKey>]?: () => any }
>
  (key: TKey, cases: TCases):
    ReturnType<TCases[keyof TCases] & Function> | undefined
{
  return cases[key]?.();
}


type ValidateKeys<Expected extends string, Provided extends string> =
  Exclude<Expected, Provided> extends never
    ? Exclude<Provided, Expected> extends never
      ? true
      : `Error: Extra keys [${Exclude<Provided, Expected>}]`
    : `Error: Missing keys [${Exclude<Expected, Provided>}]`;


type StringDiscriminatorKeys<T> = (
  {
    [K in keyof T]: (
      string extends T[K]
        ? never
        : T[K] extends string
          ? K
          : never
    );
  }[keyof T]
);


export function matchProp<
  TObject extends { [k in keyof any]: any },
  TProp extends StringDiscriminatorKeys<TObject>,
  _TKeyUnsafe extends TObject[TProp] = TObject[TProp],
  _TKeyLiteral extends string = string extends _TKeyUnsafe ? never : _TKeyUnsafe & string,
  _TCases extends {
    [k in _TKeyLiteral]: (
      record: TObject extends { [p in TProp]: infer U }
        ? U extends k ? TObject : never
        : never
    ) => any;
  } = {
    [k in _TKeyLiteral]: (
      record: TObject extends { [p in TProp]: infer U }
        ? U extends k ? TObject : never
        : never
    ) => any;
  }
>(
  obj: TObject,
  prop: TProp,
  cases: ValidateKeys<_TKeyLiteral, keyof _TCases & string> extends true
    ? _TCases
    : ValidateKeys<_TKeyLiteral, keyof _TCases & string>
): ReturnType<_TCases[keyof _TCases & string]> {
  const key = obj[prop];
  if (!(key in (cases as _TCases))) {
    throw new Error(`Key "${key}" is not handled by your cases. There is a mismatch between types and runtime value. Available cases: ${Object.keys(cases).map(k => `"${k}"`).join(", ")}.`);
  }
  return (cases as _TCases)[key](obj as any);
}


export function matchType<
  TObject extends { type: string },
  _TKeyUnsafe extends TObject["type"] = TObject["type"],
  _TKeyLiteral extends string = string extends _TKeyUnsafe ? never : _TKeyUnsafe & string,
  _TCases extends {
    [k in _TKeyLiteral]: (
      record: TObject extends { type: infer U }
        ? U extends k ? TObject : never
        : never
    ) => any;
  } = {
    [k in _TKeyLiteral]: (
      record: TObject extends { type: infer U }
        ? U extends k ? TObject : never
        : never
    ) => any;
  }
>(
  obj: TObject,
  cases: ValidateKeys<_TKeyLiteral, keyof _TCases & string> extends true
    ? _TCases
    : ValidateKeys<_TKeyLiteral, keyof _TCases & string>
): ReturnType<_TCases[keyof _TCases & string]> {
  return matchProp(obj, "type" as any, cases as any);
}


/* other utilities
 * -------------------------------------------------------------------------- */

export type Millis = number & {};


export function delay(delay: Millis) {
  return new Promise<void>(resolve => setTimeout(resolve, delay));
}


export function transform<T, U>(value: T, transform: (value: T) => U): U {
  return transform(value);
}


export function call<T>(runnable: () => T): T {
  return runnable();
}


export function when<TOption, TReturn>(
  option: TOption | undefined | null,
  then: (value: TOption) => TReturn
):
  TReturn | undefined
{
  // note: `== null` works both for null and undefined, not for other falsy values
  if (option == null) {
    return;
  }

  return then(option);
}


export function raise(errorOrMessage: Error | string): never {
  throw (
    Error.isError(errorOrMessage)
      ? errorOrMessage
      : new Error(errorOrMessage)
  );
}
